import { useToast } from '@group-link-one/gl-components';
import type { GetReportRow } from '@/Pages/Reports/types/reports.types';
import { useCallback, useEffect, useRef } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { categorizeReports } from '../utils/categorizeReports';
import { delay, downloadFile } from '../utils/downloadFile';
import { useMainLayoutTranslate } from './useMainLayoutTranslate';
import { useReportsService } from '@/Pages/Reports/Services/reportsService';
import type { ReportInfo } from '@/Stores/waiting-report.store';
import { useWaitingReportStore } from '@/Stores/waiting-report.store';

const POLLING_INTERVAL = 10 * 1000; // 10 segundos
const DOWNLOAD_DELAY = 2000; // 2 segundos entre descargas

export const useDownloadReport = () => {
  const { addToast } = useToast();
  const { customLocale } = useMainLayoutTranslate();
  const [waitingReportValue, _, removeWaitingReportValue] = useLocalStorage(
    '@ut-is-waiting-report',
    '',
  );
  const { getReports } = useReportsService();
  const {
    state: { reportInfo },
    actions: { setReportInfo, setReportError },
  } = useWaitingReportStore();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isDownloadingRef = useRef(false);

  const downloadCompletedReports = useCallback(async (reports: GetReportRow[]) => {
    for (const report of reports) {
      if (!report.download_url) continue;

      downloadFile(report.download_url);
      await delay(DOWNLOAD_DELAY);
    }
  }, []);

  const cleanup = useCallback(() => {
    setReportInfo(null);
    removeWaitingReportValue();
    setReportError(false);
    isDownloadingRef.current = false;
  }, [setReportInfo, removeWaitingReportValue, setReportError]);

  const processReports = useCallback(
    async (reportInfoArray: ReportInfo[]) => {
      try {
        isDownloadingRef.current = true;
        const { rows: reports } = await getReports({});

        const { completed, hasRunning, hasErrored } = categorizeReports(reports, reportInfoArray);

        if (hasErrored) {
          addToast({
            type: 'info',
            title: customLocale('common.error', 'Error'),
            message: customLocale(
              'common.error',
              'Some reports encountered errors during processing.',
            ),
          });
          setReportError(true);
        }

        if (hasRunning) {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);

          timeoutRef.current = setTimeout(() => {
            processReports(reportInfoArray);
          }, POLLING_INTERVAL);
          return;
        }

        await downloadCompletedReports(completed);
        cleanup();
      } catch (_error) {
        isDownloadingRef.current = false;
      }
    },
    [getReports, addToast, customLocale, setReportError, downloadCompletedReports, cleanup],
  );

  useEffect(() => {
    if (waitingReportValue) {
      try {
        const currentReport = JSON.parse(waitingReportValue) as ReportInfo[];
        setReportInfo(currentReport);
      } catch (_error) {
        removeWaitingReportValue();
      }
    }
  }, [waitingReportValue, setReportInfo, removeWaitingReportValue]);

  useEffect(() => {
    if (reportInfo && reportInfo.length > 0 && !isDownloadingRef.current) {
      processReports(reportInfo);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [reportInfo, processReports]);

  return {
    processReports,
  };
};
